import { Injectable } from '@angular/core';
import { INursingHome, ISubunit } from '@pia/pia.shared';
import { ISelectionItem } from 'src/app/common/contracts/components/selection-item';
import { NursingHomeDB, SubunitDB } from 'src/app/common/repository/databases';
import { TitlePipe } from 'src/app/patient/pipes/title.pipe';
import { PostalCodeDto } from 'src/app/shared/models/institution/postal-code-dto.model';
import { PostalCodeModelName } from 'src/app/shared/models/model-names';
import { SelectionItem } from 'src/app/shared/models/selectionItem';
import { GeneralEntityService } from 'src/app/shared/services/general-entity.service';
import { InstitutionContactFunctionService } from 'src/app/shared/services/institution-contact-function.service';
import { InstitutionContactService } from 'src/app/shared/services/institution-contact.service';
import { QueryService } from 'src/app/shared/services/query/query.service';
import { SubunitService } from 'src/app/shared/services/subunit.service';

/**
 * Return-type for getNursingHomeAddress
 */
export class NursingHomeAddress {
  public address: string;
  public additionalAddress: string;
  public city: string;
  public postalCode: string;
  public postalCodeId: string;
}

@Injectable({
  providedIn: 'root',
})
export class InstitutionDataService {
  constructor(
    private _subunitService: SubunitService,
    private _institutionContactService: InstitutionContactService,
    private _generalEntityService: GeneralEntityService,
    private _queryService: QueryService,
    private _institutionContactFuctionService: InstitutionContactFunctionService
  ) {}

  public async getInstitution<T>(
    id: string,
    entityType: GeneralEntityService['type'],
    viewModel: new () => T
  ): Promise<T> {
    if (id) {
      this._generalEntityService.type = entityType;
      const item = await this._generalEntityService.find(id);
      if (item) {
        return Promise.resolve(Object.assign(new viewModel(), item));
      }
    } else {
      return Promise.resolve(null);
    }
  }

  public async getSubunitSelectionItems(institutionId: string): Promise<ISelectionItem[]> {
    const subunits = await this._subunitService.loadSubunits(institutionId);
    const selectionItems: SelectionItem[] = [];

    if (!subunits || subunits.length === 0) {
      return undefined;
    }

    subunits
      .map(value => ({ id: value._id, name: value.name }))
      .forEach(item => {
        selectionItems.push(new SelectionItem(item.id, item.name));
      });
    return selectionItems;
  }

  public async getContactSelectionItems(institutionId: string): Promise<ISelectionItem[]> {
    const contacts = await this._institutionContactService.loadInstitutionContacts(institutionId);
    const selectionItems: SelectionItem[] = [];

    if (!contacts || contacts.length === 0) {
      return undefined;
    }
    for (const contact of contacts) {
      await contact.loadFunctionName(this._institutionContactFuctionService);
    }
    contacts
      .map(value => ({
        id: value._id,
        name: `${new TitlePipe().transform(value.title)} ${value.firstName} ${value.lastName} ${
          value.functionName ? `(${value.functionName})` : ``
        }`,
      }))
      .forEach(item => {
        selectionItems.push(new SelectionItem(item.id, item.name));
      });
    return selectionItems;
  }

  /**
   * Generate a patients address from its assigned nursing home and subunit.
   */
  public async getNursingHomeAddress(nursingHomeId: string, subunitId: string): Promise<NursingHomeAddress> {
    /*
    If the patient is residing in a nursing home, the nursing homes address is added to
    the patients contacts. If there is a selected subunit with an address, the subunits
    address will be used and the subunits name is entered in address field 2.
    */
    let nursingHomeAddress = new NursingHomeAddress();

    let nursingHome: INursingHome;
    let subunit: ISubunit;

    try {
      if (nursingHomeId) {
        nursingHome = await this._queryService.get<INursingHome>(nursingHomeId, NursingHomeDB);

        if (subunitId) {
          subunit = await this._queryService.get<ISubunit>(subunitId, SubunitDB);
        }
      }

      if (nursingHome) {
        const name = nursingHome.name;
        const nameSubunit = subunit ? subunit.name : '';
        const address = subunit && subunit.address ? subunit.address : nursingHome.address;
        const postalCode = subunit && subunit.postalCode ? subunit.postalCode : nursingHome.postalCode;
        const city = subunit && subunit.city ? subunit.city : nursingHome.city;

        nursingHomeAddress.address = address;
        nursingHomeAddress.additionalAddress = name;

        this._generalEntityService.type = PostalCodeModelName;
        let postalCodeDtos = await this._generalEntityService.query(`postalCode:${postalCode} city:${city}`);
        if (!postalCodeDtos || !postalCodeDtos.length) {
          postalCodeDtos = (await this._generalEntityService.query(`postalCode:${postalCode}`)) as PostalCodeDto[];
        }
        if (postalCodeDtos && postalCodeDtos.length) {
          nursingHomeAddress.postalCodeId = postalCodeDtos[0]._id;
          nursingHomeAddress.city = postalCodeDtos[0].city;
          nursingHomeAddress.postalCode = postalCodeDtos[0].postalCode;
        }
      } else {
        nursingHomeAddress = undefined;
      }
    } catch (error) {
      window.logger.error('failed to read nursingHome address', error);
    } finally {
      // eslint-disable-next-line no-unsafe-finally
      return nursingHomeAddress;
    }
  }
}
